import type { ComponentType } from 'react';
import { AddNoisePanel } from '../filters/AddNoisePanel';
import { BoxBlurPanel } from '../filters/BoxBlurPanel';
import { ChromaticAberrationPanel } from '../filters/ChromaticAberrationPanel';
import { EmbossPanel } from '../filters/EmbossPanel';
import { FindEdgesPanel } from '../filters/FindEdgesPanel';
import { GaussianBlurPanel } from '../filters/GaussianBlurPanel';
import { LensCorrectionPanel } from '../filters/LensCorrectionPanel';
import { MedianPanel } from '../filters/MedianPanel';
import { MotionBlurPanel } from '../filters/MotionBlurPanel';
import { PinchPanel } from '../filters/PinchPanel';
import { PixelatePanel } from '../filters/PixelatePanel';
import { PosterizePanel } from '../filters/PosterizePanel';
import { RadialBlurPanel } from '../filters/RadialBlurPanel';
import { ReduceNoisePanel } from '../filters/ReduceNoisePanel';
import { SmartSharpenPanel } from '../filters/SmartSharpenPanel';
import { ThresholdPanel } from '../filters/ThresholdPanel';
import { TwirlPanel } from '../filters/TwirlPanel';
import { UnsharpMaskPanel } from '../filters/UnsharpMaskPanel';
import { VignettePanel } from '../filters/VignettePanel';
import {
  applyAddNoiseToCanvas,
  identityAddNoiseValue,
  type AddNoiseValue,
} from '../../lib/filters/add-noise';
import {
  applyBoxBlurToCanvas,
  identityBoxBlurValue,
  type BoxBlurValue,
} from '../../lib/filters/box-blur';
import {
  applyChromaticAberrationToCanvas,
  identityChromaticAberrationValue,
  type ChromaticAberrationValue,
} from '../../lib/filters/chromatic-aberration';
import {
  applyEmbossToCanvas,
  identityEmbossValue,
  type EmbossValue,
} from '../../lib/filters/emboss';
import {
  applyFindEdgesToCanvas,
  identityFindEdgesValue,
  type FindEdgesValue,
} from '../../lib/filters/find-edges';
import {
  applyGaussianBlurToCanvas,
  identityGaussianBlurValue,
  type GaussianBlurValue,
} from '../../lib/filters/gaussian-blur';
import {
  applyLensCorrectionToCanvas,
  identityLensCorrectionValue,
  type LensCorrectionValue,
} from '../../lib/filters/lens-correction';
import {
  applyMedianToCanvas,
  identityMedianValue,
  type MedianValue,
} from '../../lib/filters/median';
import {
  applyMotionBlurToCanvas,
  identityMotionBlurValue,
  type MotionBlurValue,
} from '../../lib/filters/motion-blur';
import { applyPinchToCanvas, identityPinchValue, type PinchValue } from '../../lib/filters/pinch';
import {
  applyPixelateToCanvas,
  identityPixelateValue,
  type PixelateValue,
} from '../../lib/filters/pixelate';
import {
  applyPosterizeToCanvas,
  identityPosterizeValue,
  type PosterizeValue,
} from '../../lib/filters/posterize';
import {
  applyRadialBlurToCanvas,
  identityRadialBlurValue,
  type RadialBlurValue,
} from '../../lib/filters/radial-blur';
import {
  applyReduceNoiseToCanvas,
  identityReduceNoiseValue,
  type ReduceNoiseValue,
} from '../../lib/filters/reduce-noise';
import {
  applySmartSharpenToCanvas,
  identitySmartSharpenValue,
  type SmartSharpenValue,
} from '../../lib/filters/smart-sharpen';
import {
  applyThresholdToCanvas,
  identityThresholdValue,
  type ThresholdValue,
} from '../../lib/filters/threshold';
import { applyTwirlToCanvas, identityTwirlValue, type TwirlValue } from '../../lib/filters/twirl';
import {
  applyUnsharpMaskToCanvas,
  identityUnsharpMaskValue,
  type UnsharpMaskValue,
} from '../../lib/filters/unsharp-mask';
import {
  applyVignetteToCanvas,
  identityVignetteValue,
  type VignetteValue,
} from '../../lib/filters/vignette';
import type { AdjustmentConfig } from '../../types/image-editor.types';
import {
  applyBlackAndWhiteToCanvas,
  identityBlackAndWhiteValue,
  type BlackAndWhiteValue,
} from '../../lib/adjustments/black-white';
import {
  applyBrightnessContrastToCanvas,
  identityBrightnessContrastValue,
  type BrightnessContrastValue,
} from '../../lib/adjustments/brightness-contrast';
import {
  applyColorBalanceToCanvas,
  identityColorBalanceValue,
  type ColorBalanceValue,
} from '../../lib/adjustments/color-balance';
import {
  applyCurvesToCanvas,
  identityCurvesValue,
  type CurvesValue,
} from '../../lib/adjustments/curves';
import {
  applyHueSaturationToCanvas,
  identityHueSaturationValue,
  type HueSaturationValue,
} from '../../lib/adjustments/hue-saturation';
import {
  applyLevelsToCanvas,
  identityLevelsValue,
  type LevelsValue,
} from '../../lib/adjustments/levels';
import {
  applyShadowsHighlightsToCanvas,
  identityShadowsHighlightsValue,
  type ShadowsHighlightsValue,
} from '../../lib/adjustments/shadows-highlights';
import {
  applyVibranceToCanvas,
  identityVibranceValue,
  type VibranceValue,
} from '../../lib/adjustments/vibrance';
import { BlackAndWhitePanel } from './BlackAndWhitePanel';
import { BrightnessContrastPanel } from './BrightnessContrastPanel';
import { ColorBalancePanel } from './ColorBalancePanel';
import { CurvesPanel } from './CurvesPanel';
import { HueSaturationPanel } from './HueSaturationPanel';
import { LevelsPanel } from './LevelsPanel';
import { ShadowsHighlightsPanel } from './ShadowsHighlightsPanel';
import { VibrancePanel } from './VibrancePanel';

/** Discriminator on `AdjustmentConfig` — every implemented dialog
 *  matches one of these. Picker entries can also reference future
 *  kinds (declared in the type union but no UI yet) which render as
 *  "Coming soon". */
export type AdjustmentKind = AdjustmentConfig['kind'];

/** Common props every adjustment Panel receives from
 *  `AdjustmentsModal`. Each panel is purely controlled — it owns no
 *  state of its own beyond ephemeral UI bits like the active channel
 *  chip. The modal owns the canonical `value` (the shape that ends
 *  up serialized into the layer config) and pipes preview + apply
 *  through the spec's `applyToCanvas`. */
export interface AdjustmentPanelProps<TValue> {
  value: TValue;
  onChange: (next: TValue) => void;
  /** 256-bucket histogram of the source layer. Computed once per
   *  open by the modal and passed to whichever panels want to render
   *  it as a backdrop (Curves, Levels). Other panels ignore it. */
  histogram?: number[];
  /** Dimensions of the first source the modal captured. Set in
   *  destructive mode (always available once pipelines are built);
   *  undefined in non-destructive edit mode because the modal never
   *  captures a source there. Panels with spatial UI (e.g. the
   *  radial-blur center picker) read this to mirror the layer's
   *  aspect; panels that don't need it just ignore the prop. */
  sourceDimensions?: { width: number; height: number };
}

/** Per-kind plumbing the modal needs. Adding a new dialog = add an
 *  entry to `ADJUSTMENT_SPECS` + flip `available` to `true` in the
 *  matching `ADJUSTMENT_MENU` row. */
export interface AdjustmentSpec<TValue> {
  kind: AdjustmentKind;
  /** Title in the modal header ("Adjust — Curves"). */
  title: string;
  /** "No-change" value for this kind — used as the starting state on
   *  open and as the value re-applied when the user cancels (so the
   *  live preview resets to identity before the dialog closes). */
  identity: () => TValue;
  /** Apply `value` to `target` from the cached `source`. Must be
   *  non-cumulative — calling it repeatedly with the same value must
   *  produce the same pixels. */
  applyToCanvas: (source: ImageData, target: HTMLCanvasElement, value: TValue) => void;
  /** The body of the modal for this kind (channel selector + sliders
   *  + curve editor / etc.). Modal supplies the chrome. */
  Panel: ComponentType<AdjustmentPanelProps<TValue>>;
  /** Optional: transform `value` for the downscale preview pump so
   *  the previewed effect matches what `applyToCanvas` produces at
   *  full source resolution. The `ratio` argument is
   *  `previewSource.width / source.width` (∈ (0, 1]).
   *
   *  Continuous-radius spatial filters (Gaussian / Box / Motion /
   *  Unsharp / Smart Sharpen / Reduce Noise) implement this by
   *  scaling their `radius` / `distance` field by `ratio`. Without
   *  it, the downscale-then-upscale preview makes the filter LOOK
   *  more aggressive than the eventual Apply because pixel-radius
   *  features cover a larger fraction of the preview's smaller
   *  source.
   *
   *  Tonal/color adjustments (no spatial component) and scale-
   *  invariant filters (Radial Blur — angular sweep) omit this. */
  scaleForPreview?: (value: TValue, ratio: number) => TValue;
  /** Optional: when true, the preview pump bypasses the downscale
   *  path and applies at full source resolution. Use for filters
   *  whose effect depends on per-pixel character that cannot be
   *  recovered by scaling parameters:
   *
   *    - Median (integer-radius window — round(r * 0.375) = 0 in
   *      preview would show identity, not a milder effect)
   *    - Add Noise (per-pixel hash output depends on x/y coords —
   *      preview-space hash differs from apply-space hash by
   *      construction)
   *
   *  Trade-off: slider drag may lag visibly on big images. Worth
   *  it for these filters because the preview-vs-apply mismatch
   *  is otherwise visually jarring. */
  previewAtFullResolution?: boolean;
  /** Optional: when true, the preview pump applies `identity()` on
   *  modal open instead of waiting for the user's first slider move.
   *
   *  The default (false) is correct for adjustments whose identity
   *  produces source-unchanged output — Hue/Saturation at 0/0/0,
   *  Brightness/Contrast at 0/0, a linear Curve, etc. For those, the
   *  initial preview is already correct because the target canvases
   *  hold the captured source pixels — skipping the first-render
   *  apply saves a full pixel scan.
   *
   *  Black & White's identity is the Photoshop default mix (R40/Y60/
   *  G40/C60/B20/M80) — a meaningful transformation, NOT a no-op.
   *  Without this flag, the user sees the source in color until they
   *  move a slider. With it, the modal opens with the default mix
   *  already applied. Future adjustments whose identity isn't a
   *  source pass-through (e.g. Gradient Map, Channel Mixer w/ non-
   *  identity matrix) would set this for the same reason. */
  previewIdentityOnMount?: boolean;
}

/** Implemented kinds. `AdjustmentsModal` looks the active kind up in
 *  this map; if missing, the modal won't open (the picker renders
 *  the entry as disabled). */
// Spec values intentionally use unknown for the value generic at the
// map layer — each entry is internally well-typed, but the registry
// is heterogeneous by design. Casts to ComponentType<unknown> live at
// the dispatch site in AdjustmentsModal where the spec is fetched.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ADJUSTMENT_SPECS: Partial<Record<AdjustmentKind, AdjustmentSpec<any>>> = {
  curves: {
    kind: 'curves',
    title: 'Curves',
    identity: identityCurvesValue,
    applyToCanvas: applyCurvesToCanvas,
    Panel: CurvesPanel,
  } satisfies AdjustmentSpec<CurvesValue>,
  levels: {
    kind: 'levels',
    title: 'Levels',
    identity: identityLevelsValue,
    applyToCanvas: applyLevelsToCanvas,
    Panel: LevelsPanel,
  } satisfies AdjustmentSpec<LevelsValue>,
  'brightness-contrast': {
    kind: 'brightness-contrast',
    title: 'Brightness/Contrast',
    identity: identityBrightnessContrastValue,
    applyToCanvas: applyBrightnessContrastToCanvas,
    Panel: BrightnessContrastPanel,
  } satisfies AdjustmentSpec<BrightnessContrastValue>,
  'hue-saturation': {
    kind: 'hue-saturation',
    title: 'Hue/Saturation',
    identity: identityHueSaturationValue,
    applyToCanvas: applyHueSaturationToCanvas,
    Panel: HueSaturationPanel,
  } satisfies AdjustmentSpec<HueSaturationValue>,
  'black-white': {
    kind: 'black-white',
    title: 'Black & White',
    identity: identityBlackAndWhiteValue,
    applyToCanvas: applyBlackAndWhiteToCanvas,
    Panel: BlackAndWhitePanel,
    // Default mix is a real transformation, not a source pass-through —
    // open the dialog with the gray preview already applied.
    previewIdentityOnMount: true,
  } satisfies AdjustmentSpec<BlackAndWhiteValue>,
  'color-balance': {
    kind: 'color-balance',
    title: 'Color Balance',
    identity: identityColorBalanceValue,
    applyToCanvas: applyColorBalanceToCanvas,
    Panel: ColorBalancePanel,
  } satisfies AdjustmentSpec<ColorBalanceValue>,
  vibrance: {
    kind: 'vibrance',
    title: 'Vibrance',
    identity: identityVibranceValue,
    applyToCanvas: applyVibranceToCanvas,
    Panel: VibrancePanel,
  } satisfies AdjustmentSpec<VibranceValue>,
  'shadows-highlights': {
    kind: 'shadows-highlights',
    title: 'Shadows/Highlights',
    identity: identityShadowsHighlightsValue,
    applyToCanvas: applyShadowsHighlightsToCanvas,
    Panel: ShadowsHighlightsPanel,
  } satisfies AdjustmentSpec<ShadowsHighlightsValue>,
  // ── Tier 2 Group C — Live filters ──────────────────────────────
  // Filter kinds share `ADJUSTMENT_SPECS` because the runtime
  // pipeline treats them identically to tonal/color adjustments.
  // The UI menu split (Adjust… picker vs Filter… picker) is the
  // only place the distinction surfaces.
  'filter-gaussian-blur': {
    kind: 'filter-gaussian-blur',
    title: 'Gaussian Blur',
    identity: identityGaussianBlurValue,
    applyToCanvas: applyGaussianBlurToCanvas,
    Panel: GaussianBlurPanel,
    scaleForPreview: (v, ratio) => ({ ...v, radius: v.radius * ratio }),
  } satisfies AdjustmentSpec<GaussianBlurValue>,
  'filter-box-blur': {
    kind: 'filter-box-blur',
    title: 'Box Blur',
    identity: identityBoxBlurValue,
    applyToCanvas: applyBoxBlurToCanvas,
    Panel: BoxBlurPanel,
    scaleForPreview: (v, ratio) => ({ ...v, radius: v.radius * ratio }),
  } satisfies AdjustmentSpec<BoxBlurValue>,
  'filter-motion-blur': {
    kind: 'filter-motion-blur',
    title: 'Motion Blur',
    identity: identityMotionBlurValue,
    applyToCanvas: applyMotionBlurToCanvas,
    Panel: MotionBlurPanel,
    scaleForPreview: (v, ratio) => ({ ...v, distance: v.distance * ratio }),
  } satisfies AdjustmentSpec<MotionBlurValue>,
  'filter-radial-blur': {
    kind: 'filter-radial-blur',
    title: 'Radial Blur',
    identity: identityRadialBlurValue,
    applyToCanvas: applyRadialBlurToCanvas,
    Panel: RadialBlurPanel,
    // No scaleForPreview — radial blur is scale-invariant. The
    // angular sweep (spin) and radial-stretch range (zoom) are
    // expressed as fractions of image extent, not pixel counts, so
    // the same `amount` produces the same visual effect at any
    // resolution.
  } satisfies AdjustmentSpec<RadialBlurValue>,
  // ── PR-B: Sharpen + Noise families ─────────────────────────────
  'filter-unsharp-mask': {
    kind: 'filter-unsharp-mask',
    title: 'Unsharp Mask',
    identity: identityUnsharpMaskValue,
    applyToCanvas: applyUnsharpMaskToCanvas,
    Panel: UnsharpMaskPanel,
    // `radius` drives the underlying Gaussian; `threshold` is a
    // color delta (scale-invariant) and `amount` is a percentage.
    scaleForPreview: (v, ratio) => ({ ...v, radius: v.radius * ratio }),
  } satisfies AdjustmentSpec<UnsharpMaskValue>,
  'filter-smart-sharpen': {
    kind: 'filter-smart-sharpen',
    title: 'Smart Sharpen',
    identity: identitySmartSharpenValue,
    applyToCanvas: applySmartSharpenToCanvas,
    Panel: SmartSharpenPanel,
    scaleForPreview: (v, ratio) => ({ ...v, radius: v.radius * ratio }),
  } satisfies AdjustmentSpec<SmartSharpenValue>,
  'filter-add-noise': {
    kind: 'filter-add-noise',
    title: 'Add Noise',
    identity: identityAddNoiseValue,
    applyToCanvas: applyAddNoiseToCanvas,
    Panel: AddNoisePanel,
    // Per-pixel hash output depends on (x, y) coords — preview-space
    // and apply-space coords produce structurally different noise
    // patterns. No scaling can reconcile that; full-res preview is
    // the only honest option.
    previewAtFullResolution: true,
  } satisfies AdjustmentSpec<AddNoiseValue>,
  'filter-reduce-noise': {
    kind: 'filter-reduce-noise',
    title: 'Reduce Noise',
    identity: identityReduceNoiseValue,
    applyToCanvas: applyReduceNoiseToCanvas,
    Panel: ReduceNoisePanel,
    // `radius` is the spatial half-window; `rangeSigma` is a color
    // delta (scale-invariant) and `strength` is a blend factor.
    // Integer radius rounds to 0 only when ratio is extreme + radius
    // is 1 — at that point preview reads as identity, which is the
    // honest "less aggressive" approximation.
    scaleForPreview: (v, ratio) => ({ ...v, radius: v.radius * ratio }),
  } satisfies AdjustmentSpec<ReduceNoiseValue>,
  'filter-median': {
    kind: 'filter-median',
    title: 'Median',
    identity: identityMedianValue,
    applyToCanvas: applyMedianToCanvas,
    Panel: MedianPanel,
    // Integer radius 1..3 — scaling to a sub-pixel value would
    // round to 0 (identity preview) at any reasonable downscale
    // ratio, hiding the filter's effect entirely from preview.
    // With Huang's sliding-histogram median the full-res preview
    // runtime is bounded (~400ms per 2048² regardless of radius).
    previewAtFullResolution: true,
  } satisfies AdjustmentSpec<MedianValue>,
  // ── Stylize family (Tier 2 C wrap-up) ──────────────────────────
  'filter-emboss': {
    kind: 'filter-emboss',
    title: 'Emboss',
    identity: identityEmbossValue,
    applyToCanvas: applyEmbossToCanvas,
    Panel: EmbossPanel,
    // Fixed 3×3 kernel — scale-invariant.
  } satisfies AdjustmentSpec<EmbossValue>,
  'filter-find-edges': {
    kind: 'filter-find-edges',
    title: 'Find Edges',
    identity: identityFindEdgesValue,
    applyToCanvas: applyFindEdgesToCanvas,
    Panel: FindEdgesPanel,
    // Fixed 3×3 Sobel kernel — scale-invariant.
  } satisfies AdjustmentSpec<FindEdgesValue>,
  'filter-posterize': {
    kind: 'filter-posterize',
    title: 'Posterize',
    identity: identityPosterizeValue,
    applyToCanvas: applyPosterizeToCanvas,
    Panel: PosterizePanel,
    // Per-pixel quantization — scale-invariant.
  } satisfies AdjustmentSpec<PosterizeValue>,
  'filter-threshold': {
    kind: 'filter-threshold',
    title: 'Threshold',
    identity: identityThresholdValue,
    applyToCanvas: applyThresholdToCanvas,
    Panel: ThresholdPanel,
    // Per-pixel binarization — scale-invariant.
  } satisfies AdjustmentSpec<ThresholdValue>,
  'filter-pixelate': {
    kind: 'filter-pixelate',
    title: 'Pixelate',
    identity: identityPixelateValue,
    applyToCanvas: applyPixelateToCanvas,
    Panel: PixelatePanel,
    // Integer block size has the same rounding issue as Median's
    // integer radius — full-res preview keeps it visually honest.
    // Pixelate is also one of the cheapest filters (O(W·H)) so the
    // trade-off cost is minimal.
    previewAtFullResolution: true,
  } satisfies AdjustmentSpec<PixelateValue>,
  // ── Distort family (Tier 2 C wrap-up) ──────────────────────────
  'filter-lens-correction': {
    kind: 'filter-lens-correction',
    title: 'Lens Correction',
    identity: identityLensCorrectionValue,
    applyToCanvas: applyLensCorrectionToCanvas,
    Panel: LensCorrectionPanel,
    // Distortion math uses normalized coords (r relative to
    // half-diagonal) — scale-invariant.
  } satisfies AdjustmentSpec<LensCorrectionValue>,
  'filter-vignette': {
    kind: 'filter-vignette',
    title: 'Vignette',
    identity: identityVignetteValue,
    applyToCanvas: applyVignetteToCanvas,
    Panel: VignettePanel,
    // Normalized falloff — scale-invariant.
  } satisfies AdjustmentSpec<VignetteValue>,
  'filter-chromatic-aberration': {
    kind: 'filter-chromatic-aberration',
    title: 'Chromatic Aberration',
    identity: identityChromaticAberrationValue,
    applyToCanvas: applyChromaticAberrationToCanvas,
    Panel: ChromaticAberrationPanel,
    // `amount` is a pixel offset — scale-dependent. Scale by the
    // preview ratio so preview matches Apply.
    scaleForPreview: (v, ratio) => ({ ...v, amount: v.amount * ratio }),
  } satisfies AdjustmentSpec<ChromaticAberrationValue>,
  'filter-twirl': {
    kind: 'filter-twirl',
    title: 'Twirl',
    identity: identityTwirlValue,
    applyToCanvas: applyTwirlToCanvas,
    Panel: TwirlPanel,
    // Angular sweep + normalized radius — scale-invariant.
  } satisfies AdjustmentSpec<TwirlValue>,
  'filter-pinch': {
    kind: 'filter-pinch',
    title: 'Pinch',
    identity: identityPinchValue,
    applyToCanvas: applyPinchToCanvas,
    Panel: PinchPanel,
    // Normalized radius + dimensionless exponent — scale-invariant.
  } satisfies AdjustmentSpec<PinchValue>,
};

export type AdjustmentCategory = 'tonal' | 'color';
export type FilterCategory = 'blur' | 'sharpen' | 'noise' | 'stylize' | 'distort';

/** Picker menu — listed in the order the user sees them, grouped by
 *  category. Items whose `kind` isn't in `ADJUSTMENT_SPECS` render as
 *  disabled "Coming soon" rows. Adding a dialog =
 *    1. add the spec to `ADJUSTMENT_SPECS`
 *    2. flip the row's `available` flag here on. */
export interface AdjustmentMenuItem {
  kind: AdjustmentKind;
  title: string;
  category: AdjustmentCategory;
}

export const ADJUSTMENT_MENU: AdjustmentMenuItem[] = [
  { kind: 'curves', title: 'Curves', category: 'tonal' },
  { kind: 'levels', title: 'Levels', category: 'tonal' },
  { kind: 'brightness-contrast', title: 'Brightness/Contrast', category: 'tonal' },
  { kind: 'shadows-highlights', title: 'Shadows/Highlights', category: 'tonal' },
  { kind: 'hue-saturation', title: 'Hue/Saturation', category: 'color' },
  { kind: 'color-balance', title: 'Color Balance', category: 'color' },
  { kind: 'vibrance', title: 'Vibrance', category: 'color' },
  { kind: 'black-white', title: 'Black & White', category: 'color' },
];

export const CATEGORY_LABELS: Record<AdjustmentCategory, string> = {
  tonal: 'Tonal',
  color: 'Color',
};

/** Picker menu entry for the Filter… picker. Shape mirrors
 *  `AdjustmentMenuItem` but with the filter category union — so the
 *  two pickers share their list-rendering code while keeping their
 *  category enums (and "Coming soon" placeholders) separate. */
export interface FilterMenuItem {
  kind: AdjustmentKind;
  title: string;
  category: FilterCategory;
}

/** Filter… picker — listed in roadmap order. Items whose `kind`
 *  isn't yet in `ADJUSTMENT_SPECS` render as disabled "Soon" rows.
 *  Adding a filter dialog =
 *    1. add the spec to `ADJUSTMENT_SPECS` above
 *    2. flip the row's availability here implicitly (the picker
 *       checks `ADJUSTMENT_SPECS[item.kind]` — same pattern as the
 *       adjustments picker). */
export const FILTER_MENU: FilterMenuItem[] = [
  { kind: 'filter-gaussian-blur', title: 'Gaussian Blur', category: 'blur' },
  { kind: 'filter-motion-blur', title: 'Motion Blur', category: 'blur' },
  { kind: 'filter-radial-blur', title: 'Radial Blur', category: 'blur' },
  { kind: 'filter-box-blur', title: 'Box Blur', category: 'blur' },
  { kind: 'filter-unsharp-mask', title: 'Unsharp Mask', category: 'sharpen' },
  { kind: 'filter-smart-sharpen', title: 'Smart Sharpen', category: 'sharpen' },
  { kind: 'filter-add-noise', title: 'Add Noise', category: 'noise' },
  { kind: 'filter-reduce-noise', title: 'Reduce Noise', category: 'noise' },
  { kind: 'filter-median', title: 'Median', category: 'noise' },
  { kind: 'filter-emboss', title: 'Emboss', category: 'stylize' },
  { kind: 'filter-find-edges', title: 'Find Edges', category: 'stylize' },
  { kind: 'filter-posterize', title: 'Posterize', category: 'stylize' },
  { kind: 'filter-threshold', title: 'Threshold', category: 'stylize' },
  { kind: 'filter-pixelate', title: 'Pixelate', category: 'stylize' },
  { kind: 'filter-lens-correction', title: 'Lens Correction', category: 'distort' },
  { kind: 'filter-vignette', title: 'Vignette', category: 'distort' },
  { kind: 'filter-chromatic-aberration', title: 'Chromatic Aberration', category: 'distort' },
  { kind: 'filter-twirl', title: 'Twirl', category: 'distort' },
  { kind: 'filter-pinch', title: 'Pinch', category: 'distort' },
];

export const FILTER_CATEGORY_LABELS: Record<FilterCategory, string> = {
  blur: 'Blur',
  sharpen: 'Sharpen',
  noise: 'Noise',
  stylize: 'Stylize',
  distort: 'Distort',
};

/** User-facing display name for an adjustment or filter kind. Used
 *  for default layer names on the LayersPanel ("Curves 1",
 *  "Brightness/Contrast 2", "Gaussian Blur 3", etc.) — falls back
 *  to the kind literal for placeholder kinds that don't appear in
 *  either menu (selective-color, invert, etc.). */
export function adjustmentDisplayName(kind: AdjustmentKind): string {
  for (const item of ADJUSTMENT_MENU) if (item.kind === kind) return item.title;
  for (const item of FILTER_MENU) if (item.kind === kind) return item.title;
  return kind;
}
